import Seo from '../components/Seo';
import InquiryForm from '../components/InquiryForm';
import { brand } from '../config/brand';

const services = [
  {
    title: 'Molecular auditing',
    body: 'We assess your surfaces, soils, water hardness and workflows, then map exactly which chemistries fit — removing guesswork and redundant SKUs from your store room.',
  },
  {
    title: 'Tailored dilution configurations',
    body: 'Custom dilution charts and dosing set-ups calibrated to your equipment and tasks, so teams use the right concentration every time — no waste, no under-dosing.',
  },
  {
    title: 'Routine optimisation reports',
    body: 'Periodic reviews of usage, coverage and results, with recommendations to keep your programme efficient as your operation and seasons change.',
  },
  {
    title: 'A dedicated technical contact',
    body: 'A named point of contact who knows your account — for rapid answers, documentation, and hands-on support when it matters.',
  },
];

const steps = [
  { k: 'Audit', body: 'On-site or remote assessment of surfaces, soils, water and current programme.' },
  { k: 'Configure', body: 'A tailored chemistry map, dilution plan and documentation pack for your teams.' },
  { k: 'Deploy', body: 'Onboarding and training so the programme is adopted correctly from day one.' },
  { k: 'Optimise', body: 'Scheduled reviews and optimisation reports to sustain performance and value.' },
];

export default function PrecisionLabPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'The Precision Lab Group',
    serviceType: 'Concierge cleaning-chemistry engineering for enterprise facilities',
    provider: { '@type': 'Organization', name: brand.legalEntity, url: `${brand.siteUrl}/` },
    areaServed: 'IN',
    description:
      'An enterprise partnership from Uclipse: molecular auditing, tailored dilution configurations, and routine optimisation reports for demanding facilities.',
    url: `${brand.siteUrl}/precision-lab`,
  };

  return (
    <>
      <Seo
        title="The Precision Lab Group"
        description="Concierge engineering for enterprise facilities: molecular auditing, tailored dilution configurations, and routine optimisation reports — an ongoing partnership, not a shelf transaction."
        canonicalUrl={`${brand.siteUrl}/precision-lab`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-800 via-brand-700 to-brand-500 text-white">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="container-page relative max-w-4xl py-20 md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] backdrop-blur">
            Concierge Engineering
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight md:text-5xl lg:text-6xl">
            The Precision Lab Group
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-brand-50/90">
            Enterprise buyers don’t need another vendor — they need an engineering partner. The Precision Lab Group
            replaces reactive support with proactive, bespoke operational integration for every premium account.
          </p>
        </div>
      </section>

      {/* From support to partnership */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">
            From customer service to engineering partnership
          </h2>
          <p className="mt-3 text-slate-600">
            We don’t sell products off a shelf and disappear. We build an ongoing programme around your operation —
            measured, documented, and continuously improved.
          </p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {services.map((s) => (
            <article key={s.title} className="card p-7">
              <h3 className="font-display text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Engagement process */}
      <section className="border-y border-slate-100 bg-slate-50 py-16">
        <div className="container-page">
          <h2 className="text-center font-display text-3xl font-bold text-slate-900 md:text-4xl">
            How an engagement works
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s.k} className="relative rounded-2xl bg-white p-6 shadow-card ring-1 ring-slate-100">
                <div className="font-display text-3xl font-bold text-brand-200">{i + 1}</div>
                <h3 className="mt-1 font-display text-lg font-semibold text-slate-900">{s.k}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-2xl">
          <div className="mb-8 text-center">
            <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">
              Engage the Precision Lab Group
            </h2>
            <p className="mt-3 text-slate-600">
              Tell us about your facility and current programme. A technical partner will respond within 1 business
              day to scope an audit.
            </p>
          </div>
          <InquiryForm
            type="quote"
            title="Enterprise partnership enquiry"
            description="Share your facility type, locations, and the surfaces or challenges you’re prioritising."
          />
        </div>
      </section>
    </>
  );
}
